import React, { useEffect, useState, useRef } from 'react';
import api from '../../api';
import PropTypes from 'prop-types';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiBook, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeMap = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-slate-900/40 backdrop-blur-xs" onClick={onClose} />
        
        <div className={`relative inline-block w-full ${sizeMap[size]} p-5 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-2xl border border-slate-100 shadow-xl sm:my-8 sm:align-middle`}>
          <div className="absolute top-4 right-4 z-10">
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">
              <FiX className="w-4 h-4" />
            </button>
          </div>
          
          <div className="w-full">
            <h3 className="text-base font-bold text-slate-900 pb-3 border-b border-slate-100">{title}</h3>
            <div className="mt-4">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Notification = ({ message, type = 'success', onClose }) => {
  const bg = type === 'error' ? 'bg-rose-50/95 border-rose-200 text-rose-900' : 'bg-emerald-50/95 border-emerald-200 text-emerald-900';
  const iconColor = type === 'error' ? 'text-rose-500' : 'text-emerald-500';
  
  return (
    <div className={`fixed bottom-4 right-4 left-4 sm:left-auto z-50 max-w-sm rounded-xl p-4 shadow-md border backdrop-blur-xs flex items-start gap-3 transition-all ${bg}`} style={{ animation: 'statusSlideUp 0.25s ease-out forwards' }}>
      <div className="flex-shrink-0 mt-0.5">
        {type === 'error' ? <FiAlertCircle className={`w-4 h-4 ${iconColor}`} /> : <FiCheckCircle className={`w-4 h-4 ${iconColor}`} />}
      </div>
      <div className="flex-1">
        <p className="text-xs font-medium leading-relaxed">{message}</p>
      </div>
      <div className="flex-shrink-0">
        <button className="p-0.5 rounded-md opacity-70 hover:opacity-100 transition-opacity focus:outline-hidden" onClick={onClose}>
          <FiX className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// PROGRAM FORM
// ============================================
const ProgramForm = ({ initial = {}, onSave, onCancel }) => {
  const [name, setName] = useState(initial.name || '');
  const [duration, setDuration] = useState(initial.duration || '');
  const [type, setType] = useState(initial.type || 'full-time');
  const [description, setDescription] = useState(initial.description || '');
  const [features, setFeatures] = useState((initial.features || []).join(', '));
  const [careerPaths, setCareerPaths] = useState((initial.careerPaths || []).join(', '));
  const [recruiters, setRecruiters] = useState((initial.recruiters || []).join(', '));
  const [isActive, setIsActive] = useState(initial.isActive !== false);
  const [isFeatured, setIsFeatured] = useState(initial.isFeatured || false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      duration: duration.trim(),
      type,
      description: description.trim(),
      features: features.split(',').map(s => s.trim()).filter(Boolean),
      careerPaths: careerPaths.split(',').map(s => s.trim()).filter(Boolean),
      recruiters: recruiters.split(',').map(s => s.trim()).filter(Boolean),
      isActive,
      isFeatured
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-1 no-scrollbar">
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Program Name *</label>
        <input
          className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g., Bachelor of Computer Applications"
          required
        />
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Duration</label>
          <input
            className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            value={duration}
            onChange={e => setDuration(e.target.value)}
            placeholder="e.g., 3 years, 6 semesters"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Program Type</label>
          <select
            className="block w-full text-xs px-3 py-2 border border-slate-200 bg-white rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            value={type}
            onChange={e => setType(e.target.value)}
          >
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time</option>
            <option value="online">Online</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
        <textarea
          className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
          rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Program details summary..."
        />
      </div>

      <div className="bg-slate-50/60 p-4 rounded-xl border border-slate-200 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Features (comma separated)</label>
          <input
            className="block w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            value={features}
            onChange={e => setFeatures(e.target.value)}
            placeholder="Industry Curriculum, Expert Faculty, Projects"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Career Paths (comma separated)</label>
          <input
            className="block w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            value={careerPaths}
            onChange={e => setCareerPaths(e.target.value)}
            placeholder="Software Engineer, Data Scientist"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Recruiters (comma separated)</label>
          <input
            className="block w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            value={recruiters}
            onChange={e => setRecruiters(e.target.value)}
            placeholder="Google, Microsoft, Amazon"
          />
        </div>
      </div>

      <div className="flex items-center gap-4 py-1">
        <label className="flex items-center gap-2 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
          />
          <span className="text-xs font-semibold text-slate-700">Active</span>
        </label>
        <label className="flex items-center gap-2 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={isFeatured}
            onChange={e => setIsFeatured(e.target.checked)}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
          />
          <span className="text-xs font-semibold text-slate-700">Featured Placement</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg shadow-xs hover:bg-blue-700 transition-all">
          <FiSave className="w-3.5 h-3.5" /> <span>Save Program</span>
        </button>
      </div>
    </form>
  );
};

// ============================================
// COURSE FORM
// ============================================
const CourseForm = ({ initial = {}, onSave, onCancel }) => {
  const [name, setName] = useState(initial.name || '');
  const [duration, setDuration] = useState(initial.duration || '');
  const [description, setDescription] = useState(initial.description || '');
  const [isActive, setIsActive] = useState(initial.isActive !== false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      duration: duration.trim(),
      description: description.trim(),
      isActive
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Course Name *</label>
        <input
          className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g., Introduction to Blockchain"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Duration</label>
        <input
          className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          value={duration}
          onChange={e => setDuration(e.target.value)}
          placeholder="e.g., 6 weeks, 1 semester"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
        <textarea
          className="block w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
          rows={3}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Course focus metrics description..."
        />
      </div>
      <div>
        <label className="flex items-center gap-2 select-none cursor-pointer py-1">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
          />
          <span className="text-xs font-semibold text-slate-700">Active</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg shadow-xs hover:bg-blue-700 transition-all">
          <FiSave className="w-3.5 h-3.5" /> <span>Save Course</span>
        </button>
      </div>
    </form>
  );
};

// ============================================
// MAIN COURSE SECTION COMPONENT
// ============================================
const CourseSection = ({ tenantId = '', token = '', handleInputChange = () => {} }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notif, setNotif] = useState(null);

  const [showProgramModal, setShowProgramModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);

  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [activeProgramId, setActiveProgramId] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteTargetRef = useRef(null);

  // Set Authorization header
  useEffect(() => {
    try {
      if (token && api?.defaults?.headers?.common?.Authorization !== `Bearer ${token}`) {
        api.defaults = api.defaults || {};
        api.defaults.headers = api.defaults.headers || {};
        api.defaults.headers.common = api.defaults.headers.common || {};
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    } catch (e) {
      // fail quietly
    }
  }, [token]);

  // Fetch programs with courses
  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const resp = await api.get(`/api/website/${tenantId}/programs`);
        if (!mounted) return;
        if (resp?.data?.success) {
          const programsData = (resp.data.data || []).map(prog => ({
            ...prog,
            courses: prog.courses || []
          }));
          setPrograms(programsData);
        } else {
          setNotif({ message: 'Failed to load programs', type: 'error' });
        }
      } catch (err) {
        setNotif({ message: 'Failed to load programs', type: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [tenantId]);

  const closeNotif = () => setNotif(null);

  // ============================================
  // PROGRAM CRUD
  // ============================================
  const openCreateProgram = () => { setEditingProgram(null); setShowProgramModal(true); };
  const openEditProgram = (p) => { setEditingProgram(p); setShowProgramModal(true); };

  const saveProgram = async (payload) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      let resp;
      if (editingProgram) {
        resp = await api.put(`/api/website/${tenantId}/programs/${editingProgram._id}`, payload);
      } else {
        resp = await api.post(`/api/website/${tenantId}/programs`, payload);
      }
      if (resp?.data?.success) {
        setShowProgramModal(false);
        const listResp = await api.get(`/api/website/${tenantId}/programs`);
        if (listResp?.data?.success) {
          const programsData = (listResp.data.data || []).map(prog => ({
            ...prog,
            courses: prog.courses || []
          }));
          setPrograms(programsData);
          handleInputChange?.('programs', null, programsData);
        }
        setNotif({ message: editingProgram ? 'Program updated layout mapping' : 'Program added successfully', type: 'success' });
      } else {
        setNotif({ message: resp?.data?.message || 'Failed to save', type: 'error' });
      }
    } catch (err) {
      setNotif({ message: err.message || 'Failed to save program', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteProgram = (program) => {
    deleteTargetRef.current = { type: 'program', id: program._id };
    setShowDeleteConfirm(true);
  };

  const deleteProgram = async () => {
    const target = deleteTargetRef.current;
    if (!tenantId || !target) return;
    setLoading(true);
    try {
      if (target.type === 'program') {
        const resp = await api.delete(`/api/website/${tenantId}/programs/${target.id}`);
        if (resp?.data?.success) {
          const listResp = await api.get(`/api/website/${tenantId}/programs`);
          if (listResp?.data?.success) {
            const programsData = (listResp.data.data || []).map(prog => ({
              ...prog,
              courses: prog.courses || []
            }));
            setPrograms(programsData);
            handleInputChange?.('programs', null, programsData);
          }
          setNotif({ message: 'Program configurations removed', type: 'success' });
        } else {
          setNotif({ message: resp?.data?.message || 'Failed to delete', type: 'error' });
        }
      } else if (target.type === 'course') {
        const { programId, courseId } = target;
        const resp = await api.delete(`/api/website/${tenantId}/programs/${programId}/courses/${courseId}`);
        if (resp?.data?.success) {
          const listResp = await api.get(`/api/website/${tenantId}/programs`);
          if (listResp?.data?.success) {
            const programsData = (listResp.data.data || []).map(prog => ({
              ...prog,
              courses: prog.courses || []
            }));
            setPrograms(programsData);
            handleInputChange?.('programs', null, programsData);
          }
          setNotif({ message: 'Course profile removed from record', type: 'success' });
        } else {
          setNotif({ message: resp?.data?.message || 'Failed to delete course', type: 'error' });
        }
      }
    } catch (err) {
      setNotif({ message: err.message || 'Delete failed', type: 'error' });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      deleteTargetRef.current = null;
    }
  };

  // ============================================
  // COURSE CRUD
  // ============================================
  const openAddCourse = (programId) => {
    setActiveProgramId(programId);
    setEditingCourse(null);
    setShowCourseModal(true);
  };

  const openEditCourse = (programId, course) => {
    setActiveProgramId(programId);
    setEditingCourse(course);
    setShowCourseModal(true);
  };

  const saveCourse = async (payload) => {
    if (!tenantId || !activeProgramId) return;
    setLoading(true);
    try {
      let resp;
      if (editingCourse) {
        resp = await api.put(`/api/website/${tenantId}/programs/${activeProgramId}/courses/${editingCourse._id}`, payload);
      } else {
        resp = await api.post(`/api/website/${tenantId}/programs/${activeProgramId}/courses`, payload);
      }
      if (resp?.data?.success) {
        setShowCourseModal(false);
        const listResp = await api.get(`/api/website/${tenantId}/programs`);
        if (listResp?.data?.success) {
          const programsData = (listResp.data.data || []).map(prog => ({
            ...prog,
            courses: prog.courses || []
          }));
          setPrograms(programsData);
          handleInputChange?.('programs', null, programsData);
        }
        setNotif({ message: editingCourse ? 'Course item metrics updated' : 'Course baseline created successfully', type: 'success' });
      } else {
        setNotif({ message: resp?.data?.message || 'Failed to save course', type: 'error' });
      }
    } catch (err) {
      setNotif({ message: err.message || 'Failed to save course', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteCourse = (programId, course) => {
    deleteTargetRef.current = { type: 'course', programId, courseId: course._id };
    setShowDeleteConfirm(true);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
      {/* Header Container */}
      <div className="px-5 py-4 border-b border-slate-150 bg-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">Programs & Courses Syllabus</h2>
          <p className="text-xs text-slate-500 mt-0.5">Manage educational program fields, academic types, and internal modules mapping.</p>
        </div>
        <button
          onClick={openCreateProgram}
          disabled={!tenantId || loading}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-xs disabled:opacity-50 self-start sm:self-auto"
        >
          <FiPlus className="w-3.5 h-3.5" />
          <span>Add New Program</span>
        </button>
      </div>

      {/* Content Body Canvas */}
      <div className="p-5">
        {loading && programs.length === 0 ? (
          <div className="flex flex-col justify-center items-center py-16 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-600 border-t-transparent"></div>
            <p className="text-xs font-medium text-slate-400 animate-pulse">Loading core index values...</p>
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 bg-slate-50/50 rounded-2xl max-w-md mx-auto">
            <div className="h-10 w-10 bg-slate-100 border border-slate-200 text-slate-400 rounded-xl flex items-center justify-center mx-auto mb-3">
              <FiBook className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-bold text-slate-800">No programs mapped</h3>
            <p className="mt-1 text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">Add your organizational syllabus layout nodes to populate the website listings matrix.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {programs.map(prog => (
              <div key={prog._id} className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-300 shadow-xs transition-all flex flex-col justify-between space-y-4">
                <div>
                  {/* Row meta tags items */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <h3 className="text-sm font-bold text-slate-900 tracking-tight">{prog.name}</h3>
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                          prog.isActive !== false ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-150 text-slate-400'
                        }`}>
                          {prog.isActive !== false ? 'Active' : 'Hidden'}
                        </span>
                        {prog.isFeatured && (
                          <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-50 border border-amber-100 text-amber-700">Featured</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 font-medium">
                        {prog.duration || 'Duration open'} &bull; <span className="capitalize">{prog.type}</span>
                      </p>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditProgram(prog)}
                        className="p-1.5 text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-2xs"
                        title="Modify baseline meta info"
                      >
                        <FiEdit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => confirmDeleteProgram(prog)}
                        className="p-1.5 text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors shadow-2xs"
                        title="Delete program block"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {prog.description && <p className="mt-2.5 text-xs text-slate-500 leading-relaxed line-clamp-2">{prog.description}</p>}
                  
                  {prog.features && prog.features.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {prog.features.slice(0, 3).map((f, i) => (
                        <span key={i} className="text-[10px] bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-md font-medium">#{f}</span>
                      ))}
                      {prog.features.length > 3 && (
                        <span className="text-[10px] text-slate-400 font-medium self-center ml-1">+{prog.features.length - 3} additional tags</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Sublist Courses Panel wrapper */}
                <div className="pt-3 border-t border-slate-100/80">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Courses Core Bundle</h4>
                    <button
                      onClick={() => openAddCourse(prog._id)}
                      className="inline-flex items-center gap-0.5 px-2 py-1 bg-blue-50 text-blue-700 text-[11px] font-semibold border border-blue-100 rounded-md hover:bg-blue-100 transition-all"
                    >
                      <FiPlus className="h-3 w-3" /> <span>Add Module</span>
                    </button>
                  </div>

                  <ul className="space-y-1.5">
                    {(prog.courses || []).length === 0 && (
                      <li className="text-[11px] text-slate-400 text-center py-4 bg-slate-50/60 rounded-xl border border-dashed border-slate-150">
                        No courses added inside this catalog directory slot.
                      </li>
                    )}
                    {(prog.courses || []).map(course => (
                      <li key={course._id} className="flex items-center justify-between bg-slate-50/80 border border-slate-150 px-3 py-2 rounded-lg hover:bg-slate-50 group/item">
                        <div className="flex-1 min-w-0 pr-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-slate-800 truncate">{course.name}</span>
                            <span className={`inline-block text-[9px] font-bold px-1.5 py-0.2 rounded ${
                              course.isActive !== false ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
                            }`}>
                              {course.isActive !== false ? 'Active' : 'Off'}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 truncate mt-0.5">
                            {course.duration && <span>{course.duration}</span>}
                            {course.description && <span> &bull; {course.description}</span>}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-1 shrink-0 opacity-100 sm:opacity-0 group-hover/item:opacity-100 transition-opacity">
                          <button
                            onClick={() => openEditCourse(prog._id, course)}
                            className="p-1 text-slate-500 bg-white border border-slate-200 rounded-md hover:bg-slate-50"
                            title="Edit course metrics"
                          >
                            <FiEdit2 className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => confirmDeleteCourse(prog._id, course)}
                            className="p-1 text-white bg-rose-600 rounded-md hover:bg-rose-700"
                            title="Drop course item row"
                          >
                            <FiTrash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Program Overlay Dialog Pop-up Modal */}
      <Modal
        isOpen={showProgramModal}
        onClose={() => setShowProgramModal(false)}
        title={editingProgram ? 'Modify Syllabus Program Record' : 'Create New Educational Program Node'}
        size="lg"
      >
        <ProgramForm
          initial={editingProgram || {}}
          onSave={saveProgram}
          onCancel={() => setShowProgramModal(false)}
        />
      </Modal>

      {/* Course Overlay Dialog Pop-up Modal */}
      <Modal
        isOpen={showCourseModal}
        onClose={() => setShowCourseModal(false)}
        title={editingCourse ? 'Edit Module Component Details' : 'Attach Course Module Component'}
        size="md"
      >
        <CourseForm
          initial={editingCourse || {}}
          onSave={saveCourse}
          onCancel={() => setShowCourseModal(false)}
        />
      </Modal>

      {/* Delete Confirmation Overlay Dialog Pop-up Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Confirm structural deletion query"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            Are you completely positive you want to purge this data entry row configuration? This process drops relational row sets immediately inside database clusters.
          </p>
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
            <button
              onClick={() => { setShowDeleteConfirm(false); deleteTargetRef.current = null; }}
              className="px-3 py-1.5 text-xs font-semibold border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={deleteProgram}
              className="px-3 py-1.5 text-xs font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors shadow-xs"
            >
              Confirm Purge
            </button>
          </div>
        </div>
      </Modal>

      {/* Pop-up feedback notifications block */}
      {notif && <Notification message={notif.message} type={notif.type} onClose={closeNotif} />}
    </div>
  );
};

CourseSection.propTypes = {
  tenantId: PropTypes.string,
  token: PropTypes.string,
  handleInputChange: PropTypes.func
};

CourseSection.defaultProps = {
  tenantId: '',
  token: '',
  handleInputChange: () => {}
};

export default CourseSection;